import { action, computed, makeObservable } from 'mobx';

import type {
  TAlertNotificationSize,
  TAlertRiskLevel,
  TAlertWellGroupScope,
} from 'src/packages/alerts-preference/AlertsPersonalSettings.api';
import { requireService } from 'src/packages/di';

export class AlertsPersonalSettingsStore {
  constructor(private readonly alertsPersonalPreference = requireService('alertsPersonalPreference')) {
    makeObservable(this);
  }

  @computed
  get settings() {
    return this.alertsPersonalPreference.preferences;
  }

  @computed
  get alarmRulesShowInfoWindows() {
    return this.settings.alertTypes.alertRules.showInfoWindows;
  }

  @action.bound
  setAlarmRulesShowInfoWindows(value: boolean) {
    this.alertsPersonalPreference.updatePreferences({
      alertTypes: {
        ...this.settings.alertTypes,
        alertRules: { ...this.settings.alertTypes.alertRules, showInfoWindows: value },
      },
    });
  }

  @computed
  get alarmRulesSoundAlert() {
    return this.settings.alertTypes.alertRules.soundAlert;
  }

  @action.bound
  setAlarmRulesSoundAlert(value: boolean) {
    this.alertsPersonalPreference.updatePreferences({
      alertTypes: {
        ...this.settings.alertTypes,
        alertRules: { ...this.settings.alertTypes.alertRules, soundAlert: value },
      },
    });
  }

  @computed
  get alarmRulesShowRecommendations() {
    return this.settings.alertTypes.alertRules.showRecommendations;
  }

  @action.bound
  setAlarmRulesShowRecommendations(value: boolean) {
    this.alertsPersonalPreference.updatePreferences({
      alertTypes: {
        ...this.settings.alertTypes,
        alertRules: { ...this.settings.alertTypes.alertRules, showRecommendations: value },
      },
    });
  }

  @computed
  get manualShowInfoWindows() {
    return this.settings.alertTypes.manual.showInfoWindows;
  }

  @action.bound
  setManualShowInfoWindows(value: boolean) {
    this.alertsPersonalPreference.updatePreferences({
      alertTypes: {
        ...this.settings.alertTypes,
        manual: { ...this.settings.alertTypes.manual, showInfoWindows: value },
      },
    });
  }

  @computed
  get manualSoundAlert() {
    return this.settings.alertTypes.manual.soundAlert;
  }

  @action.bound
  setManualSoundAlert(value: boolean) {
    this.alertsPersonalPreference.updatePreferences({
      alertTypes: {
        ...this.settings.alertTypes,
        manual: { ...this.settings.alertTypes.manual, soundAlert: value },
      },
    });
  }

  @computed
  get manualShowRecommendations() {
    return this.settings.alertTypes.manual.showRecommendations;
  }

  @action.bound
  setManualShowRecommendations(value: boolean) {
    this.alertsPersonalPreference.updatePreferences({
      alertTypes: {
        ...this.settings.alertTypes,
        manual: { ...this.settings.alertTypes.manual, showRecommendations: value },
      },
    });
  }

  @computed
  get notificationSize(): TAlertNotificationSize {
    return this.settings.notificationSize;
  }

  @action.bound
  setNotificationSize(value: TAlertNotificationSize) {
    this.alertsPersonalPreference.updatePreferences({ notificationSize: value });
  }

  @computed
  get wellGroups(): TAlertWellGroupScope {
    return this.settings.wellGroups;
  }

  @action.bound
  setWellGroups(value: TAlertWellGroupScope) {
    this.alertsPersonalPreference.updatePreferences({ wellGroups: value });
  }

  @computed
  get minRisk(): TAlertRiskLevel {
    return this.settings.minRisk;
  }

  @action.bound
  setMinRisk(value: TAlertRiskLevel) {
    this.alertsPersonalPreference.updatePreferences({ minRisk: value });
  }

  @computed
  get periodTimeSeconds() {
    return this.settings.periodTimeSeconds;
  }

  @action.bound
  setPeriodTimeSeconds(value: number | null) {
    this.alertsPersonalPreference.updatePreferences({ periodTimeSeconds: value });
  }

  @computed
  get periodDepth() {
    return this.settings.periodDepth;
  }

  @action.bound
  setPeriodDepth(value: number | null) {
    this.alertsPersonalPreference.updatePreferences({ periodDepth: value });
  }

  @computed
  get durationDisplayNotificationSeconds() {
    return this.settings.durationDisplayNotificationSeconds;
  }

  @action.bound
  setDurationDisplayNotificationSeconds(value: number | null) {
    this.alertsPersonalPreference.updatePreferences({ durationDisplayNotificationSeconds: value });
  }

  @computed
  get minRiskForScreenFlicker(): TAlertRiskLevel {
    return this.settings.minRiskForScreenFlicker;
  }

  @action.bound
  setMinRiskForScreenFlicker(value: TAlertRiskLevel) {
    this.alertsPersonalPreference.updatePreferences({ minRiskForScreenFlicker: value });
  }

  @computed
  get screenFlickerCount() {
    return this.settings.screenFlickerCount;
  }

  @action.bound
  setScreenFlickerCount(value: number | null) {
    this.alertsPersonalPreference.updatePreferences({ screenFlickerCount: value });
  }

  get isLoading() {
    return this.alertsPersonalPreference.isLoading;
  }
}
